import { composeCaptionSets } from '@src/editor/models/karaoke/captionSets';
import { cloneKaraokeConfigs } from '@src/editor/models/karaoke/cloneKaraokeConfigs';
import { correctKaraokeDataFonts } from '@src/editor/services/captionFontSubstitution';
import {
  addSpaceToEndOfTranscriptElements,
  fillInWhitespacesInTranscriptElements,
} from '@src/editor/services/punctuation';
import { normalizeLangKey } from '@src/utility/bcp47';
import {
  isOriginalLang,
  ORIGINAL_LANGUAGE,
  resolveCaptionLanguageKey,
  resolveSourceLanguageKey,
} from '@src/utility/captionLanguage';
import { FONTS } from '@src/utility/general';
import { makeAutoObservable, observable } from 'mobx';

import type {
  DubRowRecord,
  KaraokeData,
  KaraokeLine,
} from '../editor/types/karaoke';
import type { CaptionSetKind } from '@src/editor/dubbing/heardDub';
import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { CaptionLineBreak } from '@src/editor/models/karaoke/CaptionLineBreak';
import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { KaraokeConfig as KaraokeConfigLegacy } from '@src/services/karaoke/karaokeTypes';

/**
 * KaraokeStore - Manages global karaoke-related state
 *
 * This store maintains karaoke state that is obtained from the VideoEditor instance.
 * It provides a centralized location for karaoke data that can be accessed across
 * the application.
 *
 * State:
 * - karaokeLines: Array of karaoke lines with transcript tokens
 * - isKaraokeGenerated: Whether karaoke has been generated
 * - isKaraokeLoading: Whether karaoke is currently being generated
 */

/**
 * PLAT-10788 — resolve a language value (the `'original'`/`'english'` sentinel
 * or a real BCP-47 code) to its canonical per-language map key. Sentinels map to
 * the session's source key; a real code is canonicalized. One chokepoint so the
 * write-back, reads, and save all key identically.
 */
function resolveActiveKey(lang: string, sourceLanguageKey: string): string {
  return isOriginalLang(lang) ? sourceLanguageKey : normalizeLangKey(lang);
}

export class KaraokeStore {
  // Core state
  /**
   * PLAT-10788 — per-language karaoke cache (BCP-47 code → variant). The active
   * language's entry is kept fresh by `syncFromEditor` (the single write-back
   * point); other languages hold their last loaded/produced variant. Populated
   * on load (Phase 4) + language switch (Phase 5); read by save
   * (`getAllKaraokeDataByLanguage`). Not deep-observable — it holds `Map`s.
   */
  loadedKaraokeDataByLanguage: Record<string, KaraokeData> = {};
  /**
   * PLAT-10788 — the active caption language: a real BCP-47 code, or the
   * `'original'`/`'english'` sentinel for the source. **Never null** — defaults
   * to `ORIGINAL_LANGUAGE` and is only replaced via `setActiveLanguage`. Resolved
   * to the canonical map key via `activeKey` (sentinel → `sourceLanguageKey`), so
   * the per-language write-back is always live (no null-inert branch).
   */
  activeLanguage: string = ORIGINAL_LANGUAGE;
  /**
   * PLAT-10788 — the canonical source-language key for this session, seeded once
   * from the transcript/story language at load (`hydrateLoadedByLanguage`, even
   * when there's no karaoke). `activeKey` uses it to resolve the `'original'`
   * sentinel. Defaults to `'en'` until the load path seeds it.
   */
  sourceLanguageKey: string = resolveSourceLanguageKey(undefined, undefined);
  legacyKaraokeConfigLoaded: KaraokeConfigLegacy | null = null;

  karaokeLines: KaraokeLine[] = [];
  karaokeConfigs: Map<string, KaraokeConfig> = new Map();
  defaultKaraokeConfig: KaraokeConfig | null = null;
  karaokeLanguage: 'original' | null = null;
  /**
   * PLAT-9623: track-level synthetic line breaks for translated captions.
   * Synced from `editor.getKaraokeTrack().syntheticBreaks` on every editor
   * sync; observable so panel UIs re-render when the user adds/removes
   * a break.
   */
  syntheticBreaks: CaptionLineBreak[] = [];

  /**
   * PLAT-11905 — mirrors of the caption track's two sets, synced with the
   * lines. `karaokeLines` is always the SHOWN set; while the dub-timed set is
   * shown, the source-timed set's lines and break records wait here, and every
   * save writes them as the top-level rows.
   */
  liveCaptionKind: CaptionSetKind = 'source';
  parkedSourceLines: KaraokeLine[] | null = null;
  parkedSourceBreaks: CaptionLineBreak[] | null = null;
  dubRecord: DubRowRecord | null = null;

  isKaraokeInitialized: boolean = false;
  isKaraokeLoading: boolean = false;

  /**
   * While a reload (e.g. undo) is in progress we keep a copy
   * of the previous lines so the UI can render them without tab flicker / scroll jump.
   */
  previousKaraokeLines: KaraokeLine[] | null = null;
  /** Transient flag indicating we are performing a source reload (e.g. undo). */
  isReloading: boolean = false;
  /**
   * PLAT-11877 — `getKaraokeLinesFormatted` memoised by the lines it was
   * built from. Every transcript render read it and got a fresh array, which
   * gave `displayElements` a new identity per render: the redline block cache
   * never hit, so leaving Redlines tore down the whole diff DOM (and rebuilt
   * every word's classes) instead of just un-hiding the text — about a
   * second on a long transcript. Not observable: a cache, not state (and
   * not `private` only because MobX's annotation map cannot name one).
   */
  formattedLinesCache: {
    source: KaraokeLine[];
    lines: KaraokeLine[];
  } | null = null;

  constructor() {
    makeAutoObservable(this, {
      loadedKaraokeData: false,
      loadedKaraokeDataByLanguage: false,
      activeKey: false,
      liveKey: false,
      formattedLinesCache: false,
      karaokeLines: observable.shallow,
      parkedSourceLines: observable.ref,
      parkedSourceBreaks: observable.ref,
      dubRecord: observable.ref,
    });
  }

  /**
   * Sync karaoke state from VideoEditor instance
   * This should be called whenever the VideoEditor state changes
   *
   * @param editor - The VideoEditor instance to sync from
   */
  syncFromEditor(editor: IVideoEditor | null) {
    if (!editor) {
      this.reset();
      return;
    }
    // Get karaoke lines from editor
    const lines = editor.getKaraokeLines();
    this.setKaraokeLines(lines);
    this.isKaraokeInitialized = true;
    this.karaokeConfigs = editor.getKaraokeConfigs();
    this.defaultKaraokeConfig = editor.getDefaultKaraokeConfig() ?? null;
    this.isReloading = false;
    this.previousKaraokeLines = null;

    if (this.karaokeConfigs.size > 100) {
      console.warn('KaraokeStore: Excessive number of karaoke configs', {
        count: this.karaokeConfigs.size,
      });
    }

    // Get karaoke track to determine if karaoke is generated
    const karaokeTrack = editor.getKaraokeTrack();
    const hasKaraokeClips = karaokeTrack
      ? karaokeTrack.elements.length > 0
      : false;
    this.syntheticBreaks = karaokeTrack?.syntheticBreaks
      ? [...karaokeTrack.syntheticBreaks]
      : [];
    this.liveCaptionKind = karaokeTrack?.liveKind ?? 'source';
    const parked = karaokeTrack?.sourceParked ?? null;
    this.parkedSourceLines = parked
      ? parked.clips.map(clip => clip.toKaraokeLine())
      : null;
    this.parkedSourceBreaks = parked ? [...parked.syntheticBreaks] : null;
    this.dubRecord = karaokeTrack?.dubRecord ?? null;

    // PLAT-10788 — single write-back point: capture the live variant into the
    // per-language cache under the key of the language it was actually produced
    // in (`liveKey`, not `activeKey`) so switching away keeps its edits.
    // `getKaraokeData` returns null when there's no karaoke, so a no-karaoke
    // video never pollutes the map.
    const live = this.getKaraokeData();
    if (live) this.loadedKaraokeDataByLanguage[this.liveKey] = live;

    console.debug('KaraokeStore: Synced from editor', {
      linesCount: lines.length,
      hasKaraokeClips,
      karaokeConfigs: Array.from(this.karaokeConfigs.entries()),
      defaultKaraokeConfigId: this.defaultKaraokeConfig?.id || null,
    });
  }

  /**
   * Set loading state for karaoke generation
   *
   * @param isLoading - Whether karaoke is loading
   */
  setIsKaraokeLoading(isLoading: boolean) {
    this.isKaraokeLoading = isLoading;
  }

  /**
   * Reset the karaoke store state
   */
  reset() {
    this.karaokeLines = [];
    this.syntheticBreaks = [];
    this.liveCaptionKind = 'source';
    this.parkedSourceLines = null;
    this.parkedSourceBreaks = null;
    this.dubRecord = null;
    this.isKaraokeInitialized = false;
    this.isKaraokeLoading = false;
    this.isReloading = false;
    this.previousKaraokeLines = null;
  }

  /**
   * Begin a reload cycle preserving current lines for UI continuity.
   */
  beginReload() {
    if (!this.isReloading) {
      this.previousKaraokeLines = this.karaokeLines.slice();
    }
    this.isReloading = true;
    this.isKaraokeInitialized = false; // mark not initialized but keep previous lines accessible
  }

  // Getters

  /**
   * Get all karaoke lines. The same array comes back while the lines it was
   * built from are the same array (`setKaraokeLines` replaces them
   * wholesale), so consumers can key memos and effects on it.
   */
  getKaraokeLinesFormatted(): KaraokeLine[] {
    const effectiveLines =
      !this.isKaraokeInitialized &&
      this.isReloading &&
      this.previousKaraokeLines
        ? this.previousKaraokeLines
        : this.karaokeLines;
    const cached = this.formattedLinesCache;
    if (cached && cached.source === effectiveLines) return cached.lines;
    const lines = effectiveLines.map(line => ({
      ...line,
      transcriptTokens: addSpaceToEndOfTranscriptElements(
        fillInWhitespacesInTranscriptElements(
          line.transcriptTokens.filter(token => token.value !== '\n'),
        ),
      ), // fix: need space to insert line break correctly in KaraokeLinesView, TODO: proper fix
    }));
    this.formattedLinesCache = { source: effectiveLines, lines };
    return lines;
  }

  /**
   * PLAT-10788 — the canonical map key for the active language (resolves the
   * `'original'` sentinel to `sourceLanguageKey`). Single keying chokepoint for
   * the write-back, `loadedKaraokeData`, and the save. Plain getter (`false` in
   * `makeAutoObservable`).
   */
  get activeKey(): string {
    return resolveActiveKey(this.activeLanguage, this.sourceLanguageKey);
  }

  /**
   * PLAT-11898 — whether saved captions with lines exist for `lang` (a code or
   * the Original sentinel), i.e. whether switching to it loads rather than
   * produces. Same rule as `switchCaptionLanguage` (DS6a: empty is a miss).
   */
  hasCaptionsIn(lang: string): boolean {
    const key = resolveActiveKey(lang, this.sourceLanguageKey);
    return (this.loadedKaraokeDataByLanguage[key]?.clips.length ?? 0) > 0;
  }

  /**
   * PLAT-10788 — the key of the variant actually loaded in the editor (synced
   * `defaultKaraokeConfig.language`); `activeKey` before the first sync.
   *
   * Which to use — is the line about what the data *is*, or what the user
   * *wants*? **Writes: `liveKey`** (the key must describe the payload; the blob
   * embeds its own `configs.language`). **Reads/selection: `activeKey`** (intent
   * — survives the editor not having produced that language yet). **DS8's load
   * preference and any `'original'`-sentinel test: raw `activeLanguage`** (both
   * getters collapse the sentinel).
   *
   * Writing by intent is what let a first produce bypassing the switch (CAPTIONS
   * button, template apply) file source-language captions under the selected
   * language's key. Plain getter (`false` in `makeAutoObservable`).
   */
  get liveKey(): string {
    const configLanguage = this.defaultKaraokeConfig?.language;
    return configLanguage
      ? resolveActiveKey(configLanguage, this.sourceLanguageKey)
      : this.activeKey;
  }

  /**
   * PLAT-10788 — the active language's cached variant (or null). Derived from the
   * per-language cache via `activeKey` so it can't drift from `activeLanguage`.
   * Plain getter (`false` in `makeAutoObservable`, not a cached computed).
   */
  get loadedKaraokeData(): KaraokeData | null {
    return this.loadedKaraokeDataByLanguage[this.activeKey] ?? null;
  }

  /**
   * PLAT-11905 — the source-timed caption lines, whichever set is shown. What
   * the out-of-date notice compares against the translation: the dub-timed
   * rows show the voice's words and never go out of date with it.
   */
  get sourceCaptionLines(): KaraokeLine[] {
    return this.liveCaptionKind === 'dub'
      ? (this.parkedSourceLines ?? [])
      : this.karaokeLines;
  }

  /**
   * The captions in their SAVED shape (every caller saves, snapshots or
   * exports): the source-timed set as the top-level rows whichever set is
   * shown, and a dubbed language's dub-timed record beside it.
   */
  getKaraokeData(): KaraokeData | null {
    if (
      !this.karaokeLines.length &&
      !this.parkedSourceLines?.length &&
      !this.loadedKaraokeData
    ) {
      return null;
    }

    if (this.isKaraokeInitialized) {
      return {
        ...composeCaptionSets({
          liveKind: this.liveCaptionKind,
          liveLines: this.karaokeLines,
          liveBreaks: this.syntheticBreaks,
          parkedSourceLines: this.parkedSourceLines,
          parkedSourceBreaks: this.parkedSourceBreaks,
          dubRecord: this.dubRecord,
        }),
        // PLAT-10788 — clone so this captured/cached variant never shares config
        // references with the editor's live map (see `cloneKaraokeConfigs`).
        configs: cloneKaraokeConfigs(this.karaokeConfigs),
        defaultConfigId: this.defaultKaraokeConfig?.id || null,
      };
    }

    return this.loadedKaraokeData;
  }

  /**
   * PLAT-10788 — set the active caption language (a real code or the
   * `'original'`/`'english'` sentinel; never null). Must run **before** a
   * load/produce so subsequent `syncFromEditor` write-backs land under the right
   * key (via `activeKey`).
   */
  setActiveLanguage(lang: string): void {
    this.activeLanguage = lang;
  }

  /**
   * PLAT-10788 — the karaoke variant for `lang`. The active language returns the
   * **live** reconstruct (latest edits); any other language returns its cached
   * variant, or `null` if none is cached yet. `lang` is resolved to its canonical
   * key so the `'original'` sentinel and a real code both work.
   */
  getKaraokeDataForLanguage(lang: string): KaraokeData | null {
    const key = resolveActiveKey(lang, this.sourceLanguageKey);
    if (key === this.activeKey && this.isKaraokeInitialized) {
      return this.getKaraokeData();
    }
    return this.loadedKaraokeDataByLanguage[key] ?? null;
  }

  /**
   * PLAT-10788 — all cached per-language variants, with the active language's
   * entry refreshed from the live editor first (so a save right after an edit
   * captures it even without a preceding `syncFromEditor`). Used by the
   * per-language save (DS2 uploads every variant). Keys via `liveKey` — the same
   * key `syncFromEditor` writes under, so a save can't file the live variant
   * under a second key.
   */
  getAllKaraokeDataByLanguage(): Record<string, KaraokeData> {
    if (this.isKaraokeInitialized) {
      const live = this.getKaraokeData();
      if (live) this.loadedKaraokeDataByLanguage[this.liveKey] = live;
    }
    return { ...this.loadedKaraokeDataByLanguage };
  }

  /**
   * PLAT-10788 — cache a variant for `lang` (e.g. after loading a persisted
   * blob or producing a language on a switch miss). `lang` is canonicalized via
   * the same `resolveActiveKey` chokepoint the lookups use (sentinel → source
   * key, `pt-br`/`ES` → `pt-BR`/`es`), so a raw/sentinel key can't be stored
   * under a form that later canonical lookups would miss.
   */
  cacheKaraokeDataForLanguage(lang: string, data: KaraokeData): void {
    const key = resolveActiveKey(lang, this.sourceLanguageKey);
    this.loadedKaraokeDataByLanguage[key] = data;
  }

  /**
   * PLAT-10788 — hydrate the per-language cache from loaded video data and pick
   * the active variant (DS8): the currently-set `activeLanguage` when a variant
   * for it exists (seeded from `?lang=` by the app shell before load — see
   * `RootLayout`), otherwise the source language, otherwise the first available
   * entry. A legacy flat blob (no map) is wrapped into a one-entry map keyed by
   * its own `config.language` (DS9). Sets `loadedKaraokeDataByLanguage` +
   * `activeLanguage` and returns the active variant for the caller to load into
   * its editor (`undefined` when there's no karaoke). Shared by the main and clip
   * load paths so DS8/DS9 lives in one place. Reads the URL nowhere — the
   * selected language is owned by `activeLanguage` (a store field).
   */
  hydrateLoadedByLanguage(args: {
    karaokeData?: KaraokeData | null;
    karaokeDataByLanguage?: Record<string, KaraokeData>;
    transcriptLanguage?: string | null;
    originalVideoLanguage?: string | null;
  }): KaraokeData | undefined {
    const {
      karaokeData,
      karaokeDataByLanguage,
      transcriptLanguage,
      originalVideoLanguage,
    } = args;

    // Seed the session source key FIRST — even on the no-karaoke path — so the
    // write-back can key the source variant before the first produce (otherwise
    // a first-produce `original` would mis-key to the `'en'` default and split
    // against the save-time `en-US`).
    const sourceKey = resolveSourceLanguageKey(
      transcriptLanguage,
      originalVideoLanguage,
    );
    this.sourceLanguageKey = sourceKey;

    let byLanguage = karaokeDataByLanguage;
    if (!byLanguage && karaokeData) {
      // DS9 — legacy flat blob → one-entry map keyed by its own config.language.
      const legacyKey = resolveCaptionLanguageKey(
        karaokeData.configs.get(karaokeData.defaultConfigId ?? '')?.language,
        transcriptLanguage,
        originalVideoLanguage,
      );
      byLanguage = { [legacyKey]: karaokeData };
    }

    if (!byLanguage || Object.keys(byLanguage).length === 0) {
      this.loadedKaraokeDataByLanguage = {};
      this.setActiveLanguage(ORIGINAL_LANGUAGE);
      return undefined;
    }

    // PLAT-11409 (epic DE4) — read-time correction of persisted variants whose
    // font can't render their language (legacy pre-epic data, e.g. Japanese
    // captions saved with Anton). A VARIANT object is never mutated (corrected
    // copies replace map entries), but the entries are replaced IN the record
    // on purpose: `byLanguage` often aliases `video.karaokeDataByLanguage`,
    // and readers of that record (publish fingerprints) must see the same
    // entries the store does. Substitution is limited to families we have
    // information about — the catalog plus the app's own `FONTS` picker list —
    // so a deliberate brand-font pick is never fought. Map keys are already
    // canonical per-language keys (PLAT-10788).
    for (const [key, variant] of Object.entries(byLanguage)) {
      const corrected = correctKaraokeDataFonts(variant, key, {
        knownFamilies: FONTS,
      });
      if (corrected !== variant) byLanguage[key] = corrected;
    }

    // DS8 — prefer the pre-seeded `activeLanguage` (from `?lang=`, resolved to
    // its canonical key) when a variant for it exists; else source; else the
    // first available entry. (A language that's selected but not yet produced
    // falls back here; the caption toggle produces it on demand.)
    const preferredKey = resolveActiveKey(this.activeLanguage, sourceKey);
    const selectedKey = byLanguage[preferredKey]
      ? preferredKey
      : byLanguage[sourceKey]
        ? sourceKey
        : Object.keys(byLanguage)[0];
    // Own a private copy: `byLanguage` is the Video record's own map, and
    // `syncFromEditor` writes the live variant into this one on every editor
    // change. Adopting it lets an edit (e.g. an aspect-ratio switch) rewrite
    // the loaded record in place — PLAT-11542. Same invariant as
    // `cloneKaraokeConfigs`, one level up.
    this.loadedKaraokeDataByLanguage = { ...byLanguage };
    this.setActiveLanguage(selectedKey);

    // PLAT-11407 — the loop above corrects each variant under ITS OWN key,
    // which is right for a variant that exists. But when the requested
    // language has no variant yet we fall back to the SOURCE one, whose font
    // was chosen for the source script — while the captions on screen are
    // still the requested language's text. Anton + Japanese is tofu, and no
    // correction had run because the source variant was judged under the
    // source key (Latin ⇒ nothing to do).
    //
    // So correct what is about to be DISPLAYED, under the key it will be
    // displayed as. Returned as a copy: the stored entry keeps the source
    // font, so switching back to the original language is unaffected.
    if (selectedKey !== preferredKey) {
      return correctKaraokeDataFonts(byLanguage[selectedKey], preferredKey, {
        knownFamilies: FONTS,
      });
    }
    return byLanguage[selectedKey];
  }

  /**
   * Get a specific karaoke line by ID
   *
   * @param lineId - The ID of the line to retrieve
   */
  getKaraokeLineById(lineId: string): KaraokeLine | undefined {
    return this.karaokeLines.find(line => line.id === lineId);
  }

  /**
   * Get the total number of karaoke lines
   */
  get lineCount(): number {
    return this.karaokeLines.length;
  }

  /**
   * Check if karaoke is ready (generated and not loading)
   */
  get isKaraokeReady(): boolean {
    return this.isKaraokeInitialized && !this.isKaraokeLoading;
  }

  /**
   * Check if there are any karaoke lines available
   */
  get hasKaraokeLines(): boolean {
    return this.karaokeLines.length > 0;
  }

  /**
   * PLAT-11689 — the caption track is produced but hidden on the video (the
   * Cap toggle). See `services/karaoke/captionsVisibility`.
   */
  get captionsHidden(): boolean {
    return this.defaultKaraokeConfig?.hidden === true;
  }

  // Private setters

  private setKaraokeLines(lines: KaraokeLine[]) {
    this.karaokeLines = lines;
  }
}
